import { Subject } from 'rxjs';
import { webSocket } from 'rxjs/webSocket';
import { makeAutoObservable } from 'mobx';
import { PriceDataStore } from './_priceDataStore';
import { ChartCandleStore } from './_chartCandleStore';
import { groupByFunction } from './utils/rxjs/groupByFunction';
import { TradePlanStore } from './_tradePlanStore';
import { TradePlanSelectionStore } from './_tradePlanSelectionStore';
import { AccountBalanceStore } from './_accountBalanceStore';
import { KillTradingStore } from './_killTradingStore';
import { ApiCallsStore } from './_apiCallsStore';
import { SymbolsLeftStore } from './_symbolsLeftStore';
import { MessageCenterStore } from './_messageCenterStore';
import { SavedTradePlanTemplatesStore } from './_savedTradePlanTemplatesStore';
import { ClosedPositionsStore } from './_closedPositionsStore';
import { ActivityLogsStore } from './_activityLogsStore';
import { WatchlistStore } from './_watchlistStore';
import { WrapTradePlanStore } from './_wrapTradePlanStore';

import { handleWebsocketClose } from './utils/handleWebsocketClose';
import { returnWebsocketUrl } from './websocketRoute';
import { LayoutStore } from './_layoutStore';
import { ApplicationTypeStore } from './_applicationTypeStore';
import { UserPreferencesStore } from './_userPreferencesStore';
import { TradierLoginStore } from './_tradierLoginStore';
import { AudioStore } from './_audioStore';
import { UserAuthStore } from './_userAuthStore';
import { emitRequestError } from './utils/requestErrorEvents';

function derivePathRequest(path) {
  return path === '/ui/alerts' ? 'ALERTS_INTERFACE' : 'TU_INTERFACE';
}

function deriveApplication(path) {
  return path === '/ui/alerts' ? 'ALERTS_APP' : 'TU_APP';
}

export class WebsocketStore {
  websocketSubject;
  tradePlanWebsocketSubject;
  websocketConnection;
  tradePlanWebsocketConnection;
  chartSubject;
  //Dylan some of the following subjects should be turned into stores. one step at a time...
  userPreferencesSubject;
  approxStrikesPerExpirationDateSubject;
  optionExpirationDatesSubject;
  tradeHistorySubject;
  quickTradeDirectionSubject;
  heartBeatSubject;
  soundAlertsSubject;
  importTemplateSubject;
  exportTemplateToHomeSubject;
  exportUserAndTemplateNamesSubject;
  displayDisclaimerAndUpdateHtmlSubject;
  orderPendingSubject;
  optionChainsSubject;
  pushNotificationsSubject;

  //stores
  applicationTypeStore;
  priceDataStore;
  chartCandleStore;
  tradePlanStore;
  tradePlanSelectionStore;
  accountBalanceStore;
  killTradingStore;
  apiCallsStore;
  symbolsLeftStore;
  messageCenterStore;
  closedPositionsStore;
  notificationsStore;
  savedTradePlanTemplatesStore;
  wrapTradePlanStore;
  layoutStore;
  userPreferencesStore;
  tradierLoginStore;
  userAuthStore;
  audioStore;
  // alertStore;
  /*
  todo in the current, we are not using this store. You will need it for the option symbols section.
   The store might need some fine tuning, but the concept should work
   */
  watchlistStore;

  connected = false;
  pathRequest = null;
  lastCloseEvent = null;
  reconnectTimers = {};
  reconnectAttempts = {
    all: 0,
    tradePlan: 0,
  };
  pendingMessages = [];
  socketConnectionState = {
    all: false,
    tradePlan: false,
  };
  // The server sends a "heartbeat" message every 60s on the 'all' socket regardless of trading
  // activity (see com.greymatter.jobs.Heartbeat). A browser WebSocket can report itself as open
  // while silently receiving nothing (a stalled tab, a half-open TCP connection) - the app then
  // keeps showing the last data it got with no indication anything is wrong. This watchdog uses
  // heartbeat silence as a liveness signal independent of the socket's own open/close state, and
  // force-reconnects when it goes quiet for too long.
  lastHeartbeatAt = Date.now();
  lastHeartbeatSkewMs = 0;
  dataStale = false;
  staleWatchdogTimer = null;
  lastStaleReconnectAt = 0;
  static STALE_THRESHOLD_MS = 150_000; // 2.5x the 60s server heartbeat interval
  static STALE_RECONNECT_COOLDOWN_MS = 30_000;

  constructor() {
    makeAutoObservable(this);
    const path = window?.location?.pathname;
    const pathRequest = derivePathRequest(path);
    this.path = pathRequest;
    this.websocketSubject = new Subject();
    this.tradePlanWebsocketSubject = new Subject();

    //initialize stores
    this.applicationTypeStore = new ApplicationTypeStore(this);
    this.tradierLoginStore = new TradierLoginStore(this);
    this.userAuthStore = new UserAuthStore(this);
    this.priceDataStore = new PriceDataStore(this);
    this.chartCandleStore = new ChartCandleStore(this);
    this.tradePlanStore = new TradePlanStore(this);
    this.tradePlanSelectionStore = new TradePlanSelectionStore(this);
    this.accountBalanceStore = new AccountBalanceStore(this);
    this.killTradingStore = new KillTradingStore(this);
    this.apiCallsStore = new ApiCallsStore(this);
    this.symbolsLeftStore = new SymbolsLeftStore(this);
    this.messageCenterStore = new MessageCenterStore(this);
    this.closedPositionsStore = new ClosedPositionsStore(this);
    this.activityLogsStore = new ActivityLogsStore(this);
    this.savedTradePlanTemplatesStore = new SavedTradePlanTemplatesStore(this);
    this.wrapTradePlanStore = new WrapTradePlanStore(this);
    this.watchlistStore = new WatchlistStore(this);
    this.layoutStore = new LayoutStore(this);
    this.userPreferencesStore = new UserPreferencesStore(this);
    this.audioStore = new AudioStore(this);
    this.websocketSubject.subscribe((msg) => this.surfaceBackendRequestErrors(msg));
    // this.alertStore = new AlertsStore(this);
    //initialize Subjects

    this.chartSubject = groupByFunction(
      'messageType',
      'getChart',
      this.websocketSubject
    );
    this.userPreferencesSubject = groupByFunction(
      'messageType',
      'userPreferences',
      this.websocketSubject
    );
    this.approxStrikesPerExpirationDateSubject = groupByFunction(
      'messageType',
      'approxStrikesPerExpirationDate',
      this.websocketSubject
    );
    this.optionExpirationDatesSubject = groupByFunction(
      'messageType',
      'optionExpirationDates',
      this.websocketSubject
    );

    this.tradeHistorySubject = groupByFunction(
      'messageType',
      'tradeHistory',
      this.websocketSubject
    );
    this.quickTradeDirectionSubject = groupByFunction(
      'messageType',
      'quickTradeDirection',
      this.websocketSubject
    );
    this.heartBeatSubject = groupByFunction(
      'messageType',
      'heartbeat',
      this.websocketSubject
    );
    this.soundAlertsSubject = groupByFunction(
      'messageType',
      'playSoundAlert',
      this.websocketSubject
    );
    this.importTemplateSubject = groupByFunction(
      'messageType',
      'importTemplate',
      this.websocketSubject
    );
    this.exportTemplateToHomeSubject = groupByFunction(
      'messageType',
      'exportTemplateToHomeResponse',
      this.websocketSubject
    );
    this.exportUserAndTemplateNamesSubject = groupByFunction(
      'messageType',
      'existingExports',
      this.websocketSubject
    );
    this.displayDisclaimerAndUpdateHtmlSubject = groupByFunction(
      'messageType',
      'disclaimer',
      this.websocketSubject
    );
    this.orderPendingSubject = groupByFunction(
      'messageType',
      'orderPendingMonitor',
      this.websocketSubject
    );
    this.optionChainsSubject = groupByFunction(
      'messageType',
      'requestOptionChains',
      this.websocketSubject
    );
    this.pushNotificationsSubject = groupByFunction(
      'messageType',
      'pushNotification',
      this.websocketSubject
    );

    this.heartBeatSubject.subscribe((msg) => {
      this.lastHeartbeatAt = Date.now();
      // msg.timeStamp is when the server generated this heartbeat. A large gap here (versus a
      // large gap in arrival) means messages ARE being delivered but late - e.g. a backgrounded
      // or throttled tab lets several heartbeats queue up and then delivers them in a burst, each
      // looking like a fresh arrival while carrying a stale payload. Track the worse of the two.
      this.lastHeartbeatSkewMs = typeof msg?.timeStamp === 'number' ? Date.now() - msg.timeStamp : 0;
      this.dataStale = false;
    });
    this.startStaleDataWatchdog();

    this.connectSockets(path);
  }
  startStaleDataWatchdog = () => {
    if (this.staleWatchdogTimer) {
      return;
    }
    this.staleWatchdogTimer = window.setInterval(() => {
      if (!this.socketConnectionState.all) {
        // Already surfaced via the disconnect banner; nothing extra to detect here.
        return;
      }
      const heartbeatAgeMs = Date.now() - this.lastHeartbeatAt;
      // lastHeartbeatSkewMs also grows while heartbeats keep arriving on time, since it's only
      // updated inside the subscription above - so checking both catches "nothing is arriving"
      // and "things are arriving, but late" (e.g. a throttled/backgrounded tab delivering a
      // backlog of queued messages, each looking like a fresh arrival with a stale payload).
      const worstAgeMs = Math.max(heartbeatAgeMs, this.lastHeartbeatSkewMs);
      if (worstAgeMs <= WebsocketStore.STALE_THRESHOLD_MS) {
        return;
      }
      this.dataStale = true;
      console.error(
        `Websocket 'all' connection is open but heartbeats are stale (arrival age=${heartbeatAgeMs}ms, payload skew=${this.lastHeartbeatSkewMs}ms) - data may be stale. Forcing reconnect.`
      );
      const now = Date.now();
      if (now - this.lastStaleReconnectAt < WebsocketStore.STALE_RECONNECT_COOLDOWN_MS) {
        return;
      }
      this.lastStaleReconnectAt = now;
      this.websocketConnection?.complete?.();
      this.socketConnectionState.all = false;
      this.connected = false;
      this.connectSocket('all', window?.location?.pathname);
    }, 20_000);
  }
  handleConnected = (socketType) => {
    console.log('connection ok');
    this.socketConnectionState[socketType] = true;
    this.connected = Object.values(this.socketConnectionState).every(Boolean);
    this.lastCloseEvent = null;
    this.reconnectAttempts[socketType] = 0;
    this.clearReconnectTimer(socketType);
    if (socketType === 'all') {
      this.lastHeartbeatAt = Date.now();
      this.lastHeartbeatSkewMs = 0;
      this.dataStale = false;
      this.flushPendingMessages();
    }
  }
  handleDisconnected = (socketType, closeEvent) => {
    this.socketConnectionState[socketType] = false;
    this.connected = Object.values(this.socketConnectionState).every(Boolean);
    this.lastCloseEvent = {
      code: closeEvent?.code ?? null,
      reason: closeEvent?.reason || '',
      wasClean: closeEvent?.wasClean ?? null,
      socketType,
      at: Date.now(),
    };
  }
  clearReconnectTimer = (socketType) => {
    if (this.reconnectTimers[socketType]) {
      window.clearTimeout(this.reconnectTimers[socketType]);
      delete this.reconnectTimers[socketType];
    }
  }
  shouldReconnect = (closeEvent) => {
    const token = localStorage.getItem('jwt_token');
    const reason = closeEvent?.reason?.toLowerCase?.() || '';
    const invalidAuth =
      closeEvent?.code === 1003 || reason.includes('invalid authentication token');

    return Boolean(token) && !invalidAuth;
  }
  scheduleReconnect = (socketType, currentPath) => {
    if (this.reconnectTimers[socketType]) {
      return;
    }

    const attempt = this.reconnectAttempts[socketType] + 1;
    this.reconnectAttempts[socketType] = attempt;
    const delay = Math.min(1000 * 2 ** (attempt - 1), 15000);

    this.reconnectTimers[socketType] = window.setTimeout(() => {
      delete this.reconnectTimers[socketType];
      this.connectSocket(socketType, currentPath);
    }, delay);
  }
  connectSockets = (currentPath) => {
    this.path = derivePathRequest(currentPath);
    this.connectSocket('all', currentPath);
    this.connectSocket('tradePlan', currentPath);
  }
  syncRoute = (currentPath) => {
    const nextPath = derivePathRequest(currentPath);
    if (nextPath === this.path) {
      return;
    }

    this.path = nextPath;
    this.pendingMessages = [];
    this.clearReconnectTimer('all');
    this.clearReconnectTimer('tradePlan');
    this.socketConnectionState = {
      all: false,
      tradePlan: false,
    };
    this.connected = false;

    this.websocketConnection?.complete?.();
    this.tradePlanWebsocketConnection?.complete?.();

    this.connectSockets(currentPath);
  }
  retryConnections = () => {
    const currentPath = window?.location?.pathname;
    this.clearReconnectTimer('all');
    this.clearReconnectTimer('tradePlan');
    this.connectSockets(currentPath);
  }
  connectSocket = (socketType, currentPath) => {
    const websocketKind = socketType === 'all' ? 'all' : 'tradePlan';
    const url = returnWebsocketUrl(this.path, websocketKind);
    const incomingSubject =
      socketType === 'all' ? this.websocketSubject : this.tradePlanWebsocketSubject;
    const connectionKey =
      socketType === 'all' ? 'websocketConnection' : 'tradePlanWebsocketConnection';

    const socket = this.buildWebsocketSubject(url, currentPath, socketType);
    this[connectionKey] = socket;

    socket.subscribe({
      next: (msg) => incomingSubject.next(msg),
      error: (error) => {
        console.error(`WebSocket ${socketType} stream error`, error);
      },
    });
  }
  surfaceBackendRequestErrors = (msg) => {
    if (!msg || msg.messageType === 'pushNotification') {
      return;
    }

    const looksLikeRequestError =
      msg.messageType === 'error' ||
      msg.messageType === 'invalidRequest' ||
      msg.messageType === 'requestError' ||
      (msg.severityScore === 'error' &&
        (msg.notification || msg.message || msg.reason || msg.error));

    if (!looksLikeRequestError) {
      return;
    }

    emitRequestError({
      symbol: msg.symbol,
      notification:
        msg.notification ||
        msg.message ||
        msg.reason ||
        msg.error ||
        'Backend rejected a websocket request.',
    });
  }
  sendWebsocketMessage = (msg) => {
    const path = window?.location?.pathname;
    const type = deriveApplication(path);
    msg.application = type;
    if (!this.websocketConnection) {
      console.warn('Queueing websocket message because the connection is not ready.', msg);
      this.pendingMessages.push(msg);
      return;
    }

    if (!this.socketConnectionState.all) {
      console.warn('Queueing websocket message while disconnected.', msg);
      this.pendingMessages.push(msg);
      return;
    }

    this.websocketConnection.next(msg);
  };

  flushPendingMessages = () => {
    if (!this.websocketConnection || !this.socketConnectionState.all) {
      return;
    }

    while (this.pendingMessages.length > 0) {
      const nextMessage = this.pendingMessages.shift();
      this.websocketConnection.next(nextMessage);
    }
  };

  buildWebsocketSubject(url, currentPath, socketType) {  //variable must be called "url"
    return webSocket({
      url,
      openObserver: {
        next: () => {
          this.handleConnected(socketType)
        },
      },
      closeObserver: {
        next: (closeEvent) => {
          console.log(closeEvent);
          this.handleDisconnected(socketType, closeEvent);
          handleWebsocketClose(closeEvent, currentPath);
          if (this.shouldReconnect(closeEvent)) {
            this.scheduleReconnect(socketType, currentPath);
          }
        },
      },
      deserializer: ({ data }) => {
        try {
          return JSON.parse(data);
        } catch (e) {
          return {};
        }
      },
      serializer: (msg) => {
        if (ArrayBuffer.isView(msg)) {
          return msg;
        }
        return JSON.stringify(msg);
      },
    });
  }

}
